/**
 * Which roles exist, and where each one belongs.
 *
 * Two spaces, two catalogues. They never overlap, and that is the point:
 *
 * - **ANEP space** — the people who run the platform, members of /Anep/{env}.
 *   SuperAdmin owns accounts and everything else; Admin runs clients,
 *   catalogue and audit without touching accounts; Operator runs the fleet —
 *   devices, templates, types, firmware — and nothing else.
 * - **Client space** — one customer organisation, members of
 *   /Clients/{uuid}/{env}. ClientAdmin manages that organisation's own
 *   technicians; Viewer looks only.
 *
 * The role says what you may do; the group says on what. Offering `Admin`
 * or `Operator` when creating a customer's technician was never valid — the
 * API refuses it — and offering `ClientAdmin` to an ANEP account grants
 * nothing, since ANEP staff are not scoped to a client.
 *
 * Mirrors `Roles` in the API's authorization.py.
 */

export type RoleSpace = 'anep' | 'client';

export interface RoleOption {
  id: string;
  name: string;
  description: string;
}

/** Roles assignable to ANEP staff. */
export const ANEP_ROLES: RoleOption[] = [
  { id: 'SuperAdmin', name: 'SuperAdmin', description: 'Full administration, including accounts and access' },
  { id: 'Admin', name: 'Admin', description: 'Clients, catalogue, templates and audit log — read-only on accounts' },
  { id: 'Operator', name: 'Operator', description: 'Runs the fleet: devices, templates, types, firmware — no clients, accounts or audit log' },
];

/** Roles assignable inside a customer organisation. */
export const CLIENT_ROLES: RoleOption[] = [
  { id: 'ClientAdmin', name: 'ClientAdmin', description: "Manages this organisation's users and devices" },
  { id: 'Viewer', name: 'Viewer', description: "Read-only on this organisation's devices" },
];

/** Roles for a space. */
export const rolesForSpace = (space: RoleSpace): RoleOption[] =>
  space === 'anep' ? ANEP_ROLES : CLIENT_ROLES;

/** Whether a role belongs to the client space. */
export const isClientRole = (role: string): boolean =>
  CLIENT_ROLES.some(r => r.id === role);

/** Whether a role belongs to the ANEP space. */
export const isAnepRole = (role: string): boolean =>
  ANEP_ROLES.some(r => r.id === role);
